import { shown } from "@/components/tools/calculus/format";
import type { DERIVATIVE_CALCULATOR_STRINGS } from "@/components/tools/derivative-calculator";
import { t } from "@/lib/i18n/interpolate";
import type { Node } from "@/lib/tool-kit/calculus/ast";
import type { DiffStep } from "@/lib/tool-kit/calculus/differentiate";

type Strings = typeof DERIVATIVE_CALCULATOR_STRINGS;

function describe(
  step: DiffStep,
  strings: Strings,
  variable: string,
  decimals: boolean
): string {
  const text = (node: Node | undefined) => (node ? shown(node, decimals) : "");
  const { parts } = step;
  const values = {
    v: variable,
    c: text(parts.constant),
    n: text(parts.constant),
    a: text(parts.constant),
    u: text(parts.u),
    w: text(parts.v),
    uPrime: text(parts.uPrime),
    wPrime: text(parts.vPrime),
    fn: step.fn ?? "",
    outer: text(parts.outer),
    count: parts.factors?.length ?? 0,
  };
  switch (step.rule) {
    case "exponential":
      return t(
        step.target.kind === "pow" &&
          step.target.base.kind === "const" &&
          step.target.base.name === "e"
          ? strings.ruleText.exponentialE
          : strings.ruleText.exponential,
        values
      );
    case "product":
      return t(
        (parts.factors?.length ?? 2) > 2
          ? strings.ruleText.productMany
          : strings.ruleText.product,
        values
      );
    default:
      return t(strings.ruleText[step.rule], values);
  }
}

/**
 * The working: one entry per subexpression, parents before children, indented by
 * depth. Each names the rule, applies it to the pieces, and states the derivative it
 * produced for that piece.
 */
export function DerivativeSteps({
  steps,
  variable,
  strings,
  decimals,
}: {
  steps: DiffStep[];
  variable: string;
  strings: Strings;
  decimals: boolean;
}) {
  return (
    <ol className="space-y-3">
      {steps.map((step, index) => (
        <li
          className="rounded-lg border border-border bg-card p-3 text-sm"
          // The steps are positional output of one derivation, never reordered.
          // biome-ignore lint/suspicious/noArrayIndexKey: positional steps
          key={index}
          style={{ marginLeft: `${Math.min(step.depth, 5) * 1.25}rem` }}
        >
          <p className="font-medium text-foreground">
            {strings.ruleTitles[step.rule]}
          </p>
          <p className="mt-1 text-muted-foreground">
            {describe(step, strings, variable, decimals)}
          </p>
          <p className="mt-2 break-words font-mono text-foreground">
            {t(strings.stepLine, {
              expression: shown(step.target, decimals),
              variable,
              result: shown(step.result, decimals),
            })}
          </p>
        </li>
      ))}
    </ol>
  );
}
